import { useState } from "react";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { reputations } from "@/mocks/manhwa";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("website_alt")?.toString().trim() || "";
    if (honeypot) {
      setFormStatus("success");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d9peh25i2vhsdh2d32m0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([k]) => k !== "website_alt")
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && (parsed as { code?: string }).code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const msg =
          (parsed as { meta?: { message?: string } })?.meta?.message ||
          (parsed as { message?: string })?.message ||
          "提交失败，请稍后重试";
        if (msg.includes("spam")) {
          setFormStatus("error");
          setFormError("提交失败，请稍后重试");
        } else {
          setFormStatus("error");
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查网络后重试");
    } finally {
      setIsSubmitting(false);
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于韩漫漫画网",
    description:
      "韩漫漫画网汇聚免费韩漫、海量漫画大全、优质在线漫画及精选韩国漫画，致力于提供专业的阅读服务。",
    about: {
      "@type": "Organization",
      name: "韩漫漫画网",
      description:
        "韩漫漫画网是一个专注于韩国漫画的在线阅读平台，汇聚免费韩漫和精选漫画大全。",
    },
  };

  return (
    <>
      <SEO
        title="关于我们 - 韩漫漫画网"
        description="了解韩漫漫画网的品牌故事、发展历程和服务理念。韩漫漫画网汇聚免费韩漫、海量漫画大全、优质在线漫画及精选韩国漫画，致力于提供专业的阅读服务。"
        keywords="韩漫漫画网,关于我们,免费韩漫,在线漫画,韩国漫画"
        canonicalUrl={typeof window !== "undefined" ? `${window.location.origin}/about` : ""}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于韩漫漫画网
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              韩漫漫画网汇聚免费韩漫、海量漫画大全、优质在线漫画及精选韩国漫画，致力于为中文读者提供专业的韩国漫画阅读服务。
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-eye-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                  我们的愿景
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  打造中文世界最优质的韩国漫画阅读平台，让每一位读者都能轻松畅享韩漫的独特魅力。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-heart-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                  我们的使命
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  持续为读者提供免费、高清、及时更新的韩漫内容，用优质服务赢得每一位漫画爱好者的信赖。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-shield-check-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                  我们的承诺
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  坚持所有漫画免费阅读，尊重版权，为读者创造安全、舒适、便捷的在线阅读环境。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
              品牌历程
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-12">
              一路走来，感谢每一位读者的陪伴
            </p>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>
              <div className="flex flex-col gap-8">
                {[
                  {
                    year: "2023",
                    title: "平台创立",
                    desc: "韩漫漫画网正式上线，以对韩国漫画的热爱为出发点，致力于为中文读者提供优质的韩漫阅读体验。",
                  },
                  {
                    year: "2024",
                    title: "快速成长",
                    desc: "平台漫画数量突破300部，月活跃用户超过20万，成为中文韩漫阅读领域的重要力量。",
                  },
                  {
                    year: "2025",
                    title: "内容升级",
                    desc: "引入更多韩国热门漫画作品，优化阅读体验，推出分类筛选和个性化推荐功能。",
                  },
                  {
                    year: "2026",
                    title: "持续创新",
                    desc: "漫画数量突破500部，活跃读者超过50万，持续拓展内容生态，提升服务质量。",
                  },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-6 items-start">
                    <div className="hidden md:flex w-16 flex-shrink-0 items-center justify-center relative z-10">
                      <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </div>
                    </div>
                    <div className="flex-1 bg-background-50 rounded-lg p-5 md:p-6">
                      <span className="inline-block text-xs font-medium text-primary-500 bg-primary-50 px-2.5 py-1 rounded mb-2">
                        {item.year}
                      </span>
                      <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
              读者口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-12">
              来自漫画爱好者的真实评价
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reputations.map((rep, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img
                        src={rep.avatar}
                        alt={rep.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{rep.name}</p>
                      <p className="text-xs text-foreground-500">{rep.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: rep.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    "{rep.content}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-lg mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              有任何问题或建议，欢迎随时与我们联系
            </p>

            {formStatus === "success" ? (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                  提交成功！
                </h3>
                <p className="text-sm text-foreground-600 mb-4">
                  感谢您的留言，我们会在24小时内回复您。
                </p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
                >
                  发送新消息
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg p-6 md:p-8"
              >
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的称呼 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的称呼"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱地址 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus === "error" && formError && (
                    <p className="text-sm text-red-500">{formError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary-500 text-white text-sm font-medium py-3 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? "提交中..." : "发送消息"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
    </>
  );
}